/**
 * AiConsultantPage — AI 健康顾问聊天主页（编排器）
 *
 * 子组件和 hooks 抽取到 ./ai-chat/ 目录
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { shouldUseMock } from '../../api/config';
import { useChatSession, DATA_SAVED_MESSAGES } from '../../features/chat/hooks';
import {
  chatApi,
  type AgentFeedbackRating,
  type Attachment,
  type ThinkingProgress,
} from '../../features/chat/api';
import { MessageContextMenu } from '../../components/MessageContextMenu';
import type { MenuPosition } from '../../components/MessageContextMenu';
import { showToast } from '../../components/Toast';
import { copyTextToClipboard } from '../../utils/clipboard';
import { saveMessageAsImage } from '../../utils/messageToImage';

import { MessageBubble } from './ai-chat/MessageBubble';
import { ChatInputArea } from './ai-chat/ChatInputArea';
import { PipelineProgress } from './ai-chat/PipelineProgress';
import { ImagePreviewModal } from './ai-chat/ImagePreviewModal';
import { ReportContextBanner } from './ai-chat/ReportContextBanner';
import { QuickQuestions } from './ai-chat/QuickQuestions';
import { SessionStatusHint } from './ai-chat/SessionStatusHint';
import { withAttachmentGrouping } from './ai-chat/attachmentGrouping';
import { MOCK_CHAT_SESSION_ID, useChatInit } from './ai-chat/useChatInit';
import { useChatQuickPrompts } from './ai-chat/useChatQuickPrompts';
import { buildContextDisplayQuestions, buildDisplayQuestions } from './ai-chat/types';

import styles from './AiConsultantPage.module.css';

const MAX_CHAT_ATTACHMENTS = 5;
const SUPPORTED_ATTACHMENT_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'pdf']);
const SUPPORTED_ATTACHMENT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
]);

function isSupportedAttachmentFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mimeType = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  if (!SUPPORTED_ATTACHMENT_EXTENSIONS.has(extension)) return false;
  return !mimeType || SUPPORTED_ATTACHMENT_MIME_TYPES.has(mimeType);
}

export function AiConsultantPage() {
  const navigate = useNavigate();
  const useMockChat = shouldUseMock('chat');
  const {
    sessionId,
    setSessionId,
    initState,
    setInitState,
    latestSession,
    setLatestSession,
    loopContext,
  } = useChatInit();
  const { quickPrompts } = useChatQuickPrompts(sessionId);
  const {
    messages,
    isTyping,
    sendMessage,
    stopGeneration,
    sessionQuickPrompts,
    dataSavedToasts,
    dismissDataSavedToast,
    resetSessionState,
    submitFeedback,
  } = useChatSession(sessionId);

  const [inputValue, setInputValue] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeContext, setActiveContext] = useState(loopContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({});
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [feedbackPendingIds, setFeedbackPendingIds] = useState<Set<string>>(() => new Set());
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const bubbleRefCallbacks = useRef<Map<string, (el: HTMLDivElement | null) => void>>(new Map());
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastScrollTimeRef = useRef(0);
  const isNearBottomRef = useRef(true);
  const previousScrollSessionRef = useRef<string | null>(null);
  const selectedAttachmentsRef = useRef(selectedAttachments);
  const pendingUploadCountRef = useRef(0);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  // Error auto-dismiss
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    setActiveContext(loopContext);
  }, [loopContext]);

  useEffect(() => {
    selectedAttachmentsRef.current = selectedAttachments;
  }, [selectedAttachments]);

  const baseDisplayQuestions = buildDisplayQuestions(sessionQuickPrompts, quickPrompts);
  const displayQuestions = buildContextDisplayQuestions(activeContext, baseDisplayQuestions);

  const activeThinkingMsg = messages.slice().reverse()
    .find(m => m.role === 'ai' && m.status && !m.text);
  const activeThinkingProgress: ThinkingProgress | undefined =
    typeof activeThinkingMsg?.status === 'object' ? activeThinkingMsg.status : undefined;
  const activeThinkingStatus: string | undefined =
    typeof activeThinkingMsg?.status === 'string' ? activeThinkingMsg.status
    : activeThinkingMsg?.status?.status;

  const displayMessages = messages.filter(m => !(m.role === 'ai' && m.status && !m.text));

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth', options?: { force?: boolean }) => {
    const now = Date.now();
    if (!options?.force && now - lastScrollTimeRef.current < 100) return;
    lastScrollTimeRef.current = now;
    messagesEndRef.current?.scrollIntoView({ behavior });
    isNearBottomRef.current = true;
    setShowJumpToLatest(false);
  }, []);

  const updateScrollIntent = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
    isNearBottomRef.current = nearBottom;
    setShowJumpToLatest(!nearBottom);
  }, []);

  const handleMessagesScroll = useCallback(() => {
    if (menuOpen) {
      setMenuOpen(false);
      setSelectedMessageId(null);
    }
    updateScrollIntent();
  }, [menuOpen, updateScrollIntent]);

  useEffect(() => {
    if (!isNearBottomRef.current) return;
    const isSessionSwitch = previousScrollSessionRef.current !== sessionId;
    previousScrollSessionRef.current = sessionId;
    const behavior: ScrollBehavior = isSessionSwitch ? 'auto' : 'smooth';
    const frame = requestAnimationFrame(() => {
      scrollToBottom(behavior, { force: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [messages, sessionId, scrollToBottom]);

  // ── Handlers ──

  const handleFilesSelected = async (files: FileList | File[]) => {
    const incomingFiles = Array.from(files ?? []);
    if (incomingFiles.length === 0) return;
    if (pendingUploadCountRef.current > 0) {
      setError('附件正在上传，请稍后再添加');
      return;
    }

    const supportedFiles = incomingFiles.filter(isSupportedAttachmentFile);
    if (supportedFiles.length === 0) {
      setError('仅支持 JPG、PNG、WebP 或 PDF 附件');
      return;
    }

    const remainingSlots = MAX_CHAT_ATTACHMENTS - selectedAttachmentsRef.current.length;
    if (remainingSlots <= 0) {
      setError('每条消息最多支持 5 个附件');
      return;
    }

    const filesToUpload = supportedFiles.slice(0, remainingSlots);
    if (filesToUpload.length < incomingFiles.length) {
      setError(
        supportedFiles.length < incomingFiles.length
          ? '已忽略不支持的附件，仅支持 JPG、PNG、WebP 或 PDF'
          : '每条消息最多支持 5 个附件，已添加前 5 个',
      );
    }

    pendingUploadCountRef.current = filesToUpload.length;
    setIsUploading(true);
    try {
      const uploaded = await Promise.all(filesToUpload.map(f => chatApi.uploadAttachment(f)));
      setSelectedAttachments(prev => {
        const slots = Math.max(0, MAX_CHAT_ATTACHMENTS - prev.length);
        return [...prev, ...withAttachmentGrouping(uploaded.slice(0, slots))];
      });
    } catch (err: any) { setError(err.message || '上传失败，请重试'); }
    finally {
      pendingUploadCountRef.current = 0;
      setIsUploading(false);
    }
  };

  const handleSendMessage = (text: string) => {
    if (isTyping) return;
    if (!text.trim() && selectedAttachments.length === 0) return;
    isNearBottomRef.current = true;
    setShowJumpToLatest(false);
    sendMessage(text.trim(), selectedAttachments.length > 0 ? selectedAttachments : undefined);
    setInputValue('');
    setSelectedAttachments([]);
    requestAnimationFrame(() => scrollToBottom('smooth', { force: true }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isTyping && e.key === 'Enter' && !e.shiftKey && (inputValue.trim() || selectedAttachments.length > 0)) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const handleNewChat = async () => {
    if (isCreating) return;
    if (isTyping) return;
    setIsCreating(true);
    try {
      if (useMockChat) {
        resetSessionState(MOCK_CHAT_SESSION_ID);
        setLatestSession(null);
        setSessionId(MOCK_CHAT_SESSION_ID);
        setInitState('new');
        setInputValue('');
        setSelectedAttachments([]);
        setActiveContext(null);
        navigate('/ask', { replace: true });
        return;
      }

      const session = await chatApi.createSession(undefined, {
        source: 'ask_new_chat',
        createdAt: new Date().toISOString(),
      });
      resetSessionState(session.id);
      setLatestSession(null);
      setSessionId(session.id);
      setInitState('new');
      setInputValue('');
      setSelectedAttachments([]);
      setActiveContext(null);
      navigate('/ask', { replace: true });
    } catch {
      setError('新建对话失败，请稍后重试');
    }
    finally { setIsCreating(false); }
  };

  // ── Context Menu ──

  const handleCloseMenu = useCallback(() => { setMenuOpen(false); setSelectedMessageId(null); }, []);
  const handlePreviewImage = useCallback((url: string) => setPreviewUrl(url), []);

  const getBubbleRef = useCallback((msgId: string) => {
    let cb = bubbleRefCallbacks.current.get(msgId);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        if (el) messageRefs.current.set(msgId, el);
        else { messageRefs.current.delete(msgId); bubbleRefCallbacks.current.delete(msgId); }
      };
      bubbleRefCallbacks.current.set(msgId, cb);
    }
    return cb;
  }, []);

  const handleLongPress = useCallback((msgId: string, event: React.TouchEvent | React.MouseEvent) => {
    const el = messageRefs.current.get(msgId);
    if (!el) return;
    const role = el.getAttribute('data-message-role');
    const isUser = role === 'user';
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const menuW = 160;
    const menuH = 80;
    let clientX: number, clientY: number;
    if ('touches' in event) { clientX = event.touches[0].clientX; clientY = event.touches[0].clientY; }
    else { clientX = event.clientX; clientY = event.clientY; }
    let position: MenuPosition = {};
    if (isUser) position.right = Math.max(16, viewportW - clientX - menuW / 2);
    else position.left = Math.max(16, clientX - menuW / 2);
    if (clientY > menuH + 16) position.bottom = Math.max(16, viewportH - clientY + 8);
    else position.top = Math.max(16, clientY + 8);
    if (position.left !== undefined && position.left + menuW > viewportW - 16) { delete position.left; position.right = 16; }
    if (position.right !== undefined && position.right + menuW > viewportW - 16) { delete position.right; position.left = 16; }
    setSelectedMessageId(msgId);
    setMenuPosition(position);
    setMenuOpen(true);
  }, []);

  const handleCopy = useCallback(() => {
    if (!selectedMessageId) return;
    const msg = displayMessages.find(m => m.id === selectedMessageId);
    if (!msg) return;
    copyTextToClipboard(msg.text).then(s => showToast(s ? '已复制' : '复制失败，请重试'));
  }, [selectedMessageId, displayMessages]);

  const handleSaveImage = useCallback(() => {
    if (!selectedMessageId) return;
    const el = messageRefs.current.get(selectedMessageId);
    if (!el) return;
    saveMessageAsImage(el, `message-${selectedMessageId.slice(0, 8)}.png`).then(s => showToast(s ? '已保存' : '保存失败'));
  }, [selectedMessageId]);

  const handleFeedback = useCallback((messageId: string, value: AgentFeedbackRating) => {
    if (feedbackPendingIds.has(messageId)) return;
    setFeedbackPendingIds((prev) => new Set(prev).add(messageId));
    submitFeedback(messageId, value)
      .then(() => {
        showToast(value === 'helpful' ? '已标记：有帮助' : '已记录反馈');
      })
      .catch(() => {
        setError('反馈提交失败，请稍后重试');
      })
      .finally(() => {
        setFeedbackPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      });
  }, [feedbackPendingIds, submitFeedback]);

  // ── Render ──

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <PageHeader
        mode="primary"
        className={styles.chatHeader}
        title="解惑"
        subtitle="AI 循证顾问"
        showProfile
        profileHref="/profile"
        actions={[
          { icon: 'plus', onClick: handleNewChat, label: '新对话' },
          { icon: 'clock', onClick: () => navigate('/ask/sessions'), label: '历史会话' },
        ]}
      />

      <AnimatePresence>
        {error && (
          <motion.div
            className={styles.errorBanner}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <SessionStatusHint initState={initState} latestSession={latestSession} />

      {activeContext && (
        <ReportContextBanner
          context={activeContext}
          onClose={() => setActiveContext(null)}
        />
      )}

      {/* DataSaved toasts — subtle micro-feedback below the header */}
      <AnimatePresence>
        {dataSavedToasts.map((toast, idx) => (
          <motion.div
            key={`${toast.dataType}-${toast.key}-${idx}`}
            className={styles.dataSavedToast}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={() => dismissDataSavedToast(idx)}
          >
            {toast.dataType === 'fhir'
              ? <Activity className={styles.dataSavedIcon} />
              : <Brain className={styles.dataSavedIcon} />}
            <span className={styles.dataSavedText}>
              {DATA_SAVED_MESSAGES[toast.dataType] || '已保存'}
              {toast.detail ? ` · ${toast.detail}` : ''}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>

      <QuickQuestions questions={displayQuestions} onSelect={handleSendMessage} disabled={isTyping} />

      <div
        ref={messagesContainerRef}
        className={styles.messagesContainer}
        onScroll={handleMessagesScroll}
      >
        <AnimatePresence mode="popLayout">
          {displayMessages.map(msg => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              onLongPress={handleLongPress}
              onPreviewImage={handlePreviewImage}
              onAskFollowup={handleSendMessage}
              onFeedback={handleFeedback}
              feedbackPending={feedbackPendingIds.has(msg.id)}
              bubbleRef={getBubbleRef(msg.id)}
            />
          ))}
        </AnimatePresence>

        {activeThinkingStatus && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles.typingIndicator}
            aria-live="polite"
            aria-atomic="true"
          >
            <div className={styles.typingBubble}>
              {activeThinkingProgress ? (
                <PipelineProgress thinking={activeThinkingProgress} />
              ) : (
                <span className={styles.thinkingStatus}>
                  <motion.span
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    {activeThinkingStatus}
                  </motion.span>
                </span>
              )}
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <AnimatePresence>
        {showJumpToLatest && (
          <motion.button
            type="button"
            className={styles.jumpToLatestButton}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={() => scrollToBottom('smooth', { force: true })}
          >
            回到最新
          </motion.button>
        )}
      </AnimatePresence>

      <ChatInputArea
        inputValue={inputValue}
        onInputChange={setInputValue}
        selectedAttachments={selectedAttachments}
        isUploading={isUploading}
        isTyping={isTyping}
        onSend={handleSendMessage}
        onStop={stopGeneration}
        onFilesSelected={handleFilesSelected}
        onRemoveAttachment={idx => setSelectedAttachments(prev => prev.filter((_, i) => i !== idx))}
        onKeyDown={handleKeyDown}
      />

      {previewUrl && <ImagePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />}

      <MessageContextMenu
        open={menuOpen}
        position={menuPosition}
        onCopy={handleCopy}
        onSaveImage={handleSaveImage}
        onClose={handleCloseMenu}
      />
    </motion.div>
  );
}
